import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PurchaseReturnDocumentCandidate,
  PurchaseReturnEligibility,
  PurchaseReturnPreview,
} from '../../../types/fiscal/PurchaseReturnEvent';

import { FiscalEventSubmission } from '../../../types/fiscal/FiscalEvent';

// =====================================================
// 📦 Tipos
// =====================================================

export interface SelectedPurchaseReturnDocument {
  candidate?: PurchaseReturnDocumentCandidate;
  eligibility: PurchaseReturnEligibility;
}

interface PurchaseReturnEventState {
  documents: SelectedPurchaseReturnDocument[];

  selectedLines: Record<string, boolean>;

  quantities: Record<string, string>;
  unitCosts: Record<string, string>;
  discounts: Record<string, string>;

  preview: PurchaseReturnPreview | null;
  previewLoading: boolean;

  result: FiscalEventSubmission | null;

  busy: boolean;
  error: string | null;
}

// =====================================================
// 🏁 Estado inicial
// =====================================================

const initialState: PurchaseReturnEventState = {
  documents: [],

  selectedLines: {},
  quantities: {},
  unitCosts: {},
  discounts: {},

  preview: null,
  previewLoading: false,

  result: null,

  busy: false,
  error: null,
};

// =====================================================
// 🧩 Slice
// =====================================================

const purchaseReturnEventSlice = createSlice({
  name: 'purchaseReturnEvent',

  initialState,

  reducers: {
    addPurchaseReturnDocument(state, action: PayloadAction<SelectedPurchaseReturnDocument>) {
      const exists = state.documents.some(
        (document) => document.eligibility.purchaseId === action.payload.eligibility.purchaseId
      );

      if (!exists) {
        state.documents.push(action.payload);
      }
    },

    removePurchaseReturnDocument(state, action: PayloadAction<number>) {
      const purchaseId = action.payload;

      state.documents = state.documents.filter(
        (document) => document.eligibility.purchaseId !== purchaseId
      );

      const prefix = `${purchaseId}:`;

      state.selectedLines = Object.fromEntries(
        Object.entries(state.selectedLines).filter(([key]) => !key.startsWith(prefix))
      );

      state.quantities = Object.fromEntries(
        Object.entries(state.quantities).filter(([key]) => !key.startsWith(prefix))
      );

      state.unitCosts = Object.fromEntries(
        Object.entries(state.unitCosts).filter(([key]) => !key.startsWith(prefix))
      );

      state.discounts = Object.fromEntries(
        Object.entries(state.discounts).filter(([key]) => !key.startsWith(prefix))
      );

      state.preview = null;
    },

    setPurchaseReturnLineSelected(
      state,
      action: PayloadAction<{
        key: string;
        selected: boolean;
        quantity?: string;
        unitCost?: string;
        discount?: string;
      }>
    ) {
      const { key, selected, quantity, unitCost, discount } = action.payload;

      state.selectedLines[key] = selected;

      state.quantities[key] = selected ? quantity ?? state.quantities[key] ?? '' : '';
      state.unitCosts[key] = selected ? unitCost ?? state.unitCosts[key] ?? '' : '';
      state.discounts[key] = selected ? discount ?? state.discounts[key] ?? '' : '';

      state.preview = null;
    },

    setPurchaseReturnLineQuantity(state, action: PayloadAction<{ key: string; quantity: string }>) {
      state.quantities[action.payload.key] = action.payload.quantity;
      state.preview = null;
    },

    setPurchaseReturnLineUnitCost(state, action: PayloadAction<{ key: string; unitCost: string }>) {
      state.unitCosts[action.payload.key] = action.payload.unitCost;
      state.preview = null;
    },

    setPurchaseReturnLineDiscount(state, action: PayloadAction<{ key: string; discount: string }>) {
      state.discounts[action.payload.key] = action.payload.discount;
      state.preview = null;
    },

    setPurchaseReturnDocumentLines(
      state,
      action: PayloadAction<{
        purchaseId: number;
        lines: Array<{
          productId: number;
          availableQuantity: number;
          unitCost: number;
          discount: number;
        }>;
        selected: boolean;
      }>
    ) {
      const { purchaseId, lines, selected } = action.payload;

      lines.forEach((line) => {
        const key = `${purchaseId}:${line.productId}`;

        state.selectedLines[key] = selected;
        state.quantities[key] = selected ? String(line.availableQuantity) : '';
        state.unitCosts[key] = selected ? String(line.unitCost) : '';
        state.discounts[key] = selected ? String(line.discount) : '';
      });

      state.preview = null;
    },

    setPurchaseReturnPreview(state, action: PayloadAction<PurchaseReturnPreview | null>) {
      state.preview = action.payload;
    },

    clearPurchaseReturnPreview(state) {
      state.preview = null;
    },

    setPurchaseReturnPreviewLoading(state, action: PayloadAction<boolean>) {
      state.previewLoading = action.payload;
    },

    setPurchaseReturnResult(state, action: PayloadAction<FiscalEventSubmission | null>) {
      state.result = action.payload;
    },

    setPurchaseReturnBusy(state, action: PayloadAction<boolean>) {
      state.busy = action.payload;
    },

    setPurchaseReturnError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    resetPurchaseReturnEvent(state) {
      state.documents = [];

      state.selectedLines = {};
      state.quantities = {};
      state.unitCosts = {};
      state.discounts = {};

      state.preview = null;
      state.previewLoading = false;

      state.result = null;

      state.busy = false;
      state.error = null;
    },
  },
});

// =====================================================
// 📤 Actions
// =====================================================

export const {
  addPurchaseReturnDocument,
  removePurchaseReturnDocument,

  setPurchaseReturnLineSelected,
  setPurchaseReturnLineQuantity,
  setPurchaseReturnLineUnitCost,
  setPurchaseReturnLineDiscount,
  setPurchaseReturnDocumentLines,

  setPurchaseReturnPreview,
  clearPurchaseReturnPreview,
  setPurchaseReturnPreviewLoading,

  setPurchaseReturnResult,

  setPurchaseReturnBusy,
  setPurchaseReturnError,

  resetPurchaseReturnEvent,
} = purchaseReturnEventSlice.actions;

// =====================================================
// 📤 Reducer
// =====================================================

export default purchaseReturnEventSlice.reducer;
